import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewContainerRef, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';

import { SkinningService } from '@core/skin';
import { KeyboardService } from '@core/keyboard';
import { LayoutStore, NavigationUiService } from '@core/layout';
import { GoogleOneTapService } from '@core/auth';
import { AppVersionService, BootRedirectService, PwaUpdateService, WebViewNavigationGuardService } from '@core/common';
import { LocalizationStore } from '@core/localization';
import { UxService } from '@core/ux';
import { DeviceService } from '@core/device';
import { SeoService } from '@core/seo';
import { AnalyticEvent, AnalyticsService } from '@core/analytics';

import { AssetSearchService } from '@features/assets/data-access';
import { ItemMiniDetailsService } from '@features/mini-details/data-access/mini-details.service';
import { PlayerService } from '@features/player/data-access/player.service';
import { ScrollDepthAnalytics } from '@shared/directives/scroll-depth-analytics.directive';

import { MainLayoutComponent } from '@core/layout/main-layout/main-layout.component';
import { DetailsShell } from '@features/details';
import { AssetSearchComponent } from '@features/assets/components/asset-search/asset-search.component';
import { Splash } from './shared/components/splash/splash.component';
import { AppSpinnerComponent } from '@shared/components/app-spinner/app-spinner.component';
import { Modal } from './shared/components/modal/modal.component';
import { MiniDetails } from '@features/mini-details/components/mini-details.component';
import { PlayerComponent } from '@features/player';
import { PlayerLoadingSpinnerComponent } from './features/player/components/player-loading-spinner/player-loading-spinner.component';

// Thin application shell. All the former god-component responsibilities now live
// in focused collaborators:
//   - router-event side effects + splash teardown → NavigationUiService
//   - header/footer + programme state              → LayoutStore
//   - Google One Tap                                → GoogleOneTapService
//   - first-paint boot redirect                     → BootRedirectService
//   - scroll-depth analytics                        → ScrollDepthAnalytics (hostDirective)
// What stays here is genuinely component-bound: hosting the global overlays and
// wiring the player's template/view refs after the view initialises.
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    MainLayoutComponent,
    DetailsShell,
    AssetSearchComponent,
    Splash,
    AppSpinnerComponent,
    Modal,
    MiniDetails,
    PlayerComponent,
    PlayerLoadingSpinnerComponent,
  ],
  hostDirectives: [ScrollDepthAnalytics],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly layout = inject(LayoutStore);
  public readonly skinning = inject(SkinningService);

  private readonly router = inject(Router);
  private readonly ux = inject(UxService);
  private readonly deviceService = inject(DeviceService);
  private readonly localizations = inject(LocalizationStore);
  private readonly searchService = inject(AssetSearchService);
  private readonly itemMiniDetailsService = inject(ItemMiniDetailsService);
  private readonly playerService = inject(PlayerService);
  private readonly seoService = inject(SeoService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly bootRedirect = inject(BootRedirectService);

  public readonly expandedMiniItem = this.itemMiniDetailsService.expandedMiniItem;
  public readonly search = signal(false);
  public isMobile = false;

  private readonly playerTemplate = viewChild.required<TemplateRef<unknown>>('playerTemplate');
  private readonly playerContainer = viewChild('playerContainer', { read: ViewContainerRef });

  constructor() {
    // Instantiated for their side effects: NavigationUiService subscribes to
    // router events (MUST be live before the boot redirect so the first
    // NavigationEnd is observed); GoogleOneTapService arms the anonymous One Tap
    // effect.
    inject(NavigationUiService);
    inject(GoogleOneTapService);
    // Initialize global keyboard handling (previously triggered by SharedService's constructor).
    inject(KeyboardService).initialize();
    // Arm the service-worker update prompt (no-op when the SW is disabled).
    // Per-operator install branding is baked at build time via a static
    // manifest overlay (see angular.json), not swapped in at runtime.
    inject(PwaUpdateService).initialize();
    // Reloads onto the version the BE declares current when this build is older (37535).
    inject(AppVersionService).initialize();

    this.seoService.registerOnUrlEvents();
    // Dynamically add Google Analytics script to the document head.
    this.analyticsService.initialize();
    // Seed header visibility from the boot URL so the first paint of the global
    // chrome is already correct on a cold reload — NavigationUiService keeps it
    // correct on every subsequent navigation.
    this.layout.applyForUrl(window.location.pathname);
    // Must be live before the initial navigation so a WebView deep link stays pinned to its page.
    inject(WebViewNavigationGuardService).run();
    this.bootRedirect.run();
  }

  ngOnInit(): void {
    setTimeout(() => this.ux.setAdditionalHeaderStyles());
    this.localizations.loadLanguages().subscribe();
    this.isMobile = this.deviceService.isMobile();
  }

  ngAfterViewInit(): void {
    this.playerService.setPlayerTemplateRef(this.playerTemplate());
    this.playerService.setDefaultPlayerViewRef(this.playerContainer()!);
    this.playerService.changePlayerViewLocation();

    // Reveal the header once boot styling has settled (header CSS keys off this).
    setTimeout(() => {
      document.querySelector('header')?.setAttribute('data-loading', 'false');
    }, 1000);
  }

  ngOnDestroy(): void {
    this.analyticsService.logEvent(AnalyticEvent.SessionDuration);
  }

  goHome(): void {
    if (this.ux.searching) {
      this.ux.closeSearch();
    }
    this.router.navigate(['home'], { state: { explicitNavigation: true } });
  }

  // Public: also called by ErrorPage to force the search overlay closed.
  searchOpened(visible: boolean): void {
    this.searchService.isVisible.set(visible);
    this.search.set(visible);
    const content = document.querySelector('.content') as HTMLElement | null;
    if (content) {
      content.style.display = visible ? 'none' : 'block';
    }
  }
}
